import type { Job, Watch, WatchCondition, WatchNotifyTarget, WatchStatus } from "@/lib/types";

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "<1m ago";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

export function formatAbsoluteTime(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso),
  );
}

export function getWatchTitle(watch: Watch): string {
  return watch.entityIdentifier ?? `All ${watch.entityType.replace(/_/g, " ")}s`;
}

export function describeWatchCondition(condition: WatchCondition): string {
  if (condition.type === "state_change") {
    const from = getString(condition.fromState);
    const to = getString(condition.toState);
    if (from && to) return `When it moves from ${from} to ${to}`;
    if (to) return `When it moves to ${to}`;
    if (from) return `When it leaves ${from}`;
  }
  const label = getString(condition.label);
  if (condition.type === "label_added" && label) return `When label ${label} is added`;
  const assignee = getString(condition.assignee);
  if (condition.type === "assignee_changed" && assignee) {
    return `When assignee changes to ${assignee}`;
  }
  if (condition.type === "message_posted") return "When a matching Slack message is posted";
  return condition.type.replace(/_/g, " ");
}

export function describeNotifyTarget(target: WatchNotifyTarget): string {
  switch (target.type) {
    case "linear_thread":
      return "Reply in the Linear thread";
    case "slack_channel":
      return `Post in Slack ${target.channel}`;
    case "slack_thread":
      return `Reply in Slack thread ${target.channel}`;
    case "slack_trigger_thread":
      return "Reply in the triggering Slack thread";
    case "slack_dm":
      return `Send a Slack DM${target.email || target.userId ? ` to ${target.email ?? target.userId}` : ""}`;
  }
}

export function getWatchStatusTone(status: WatchStatus): string {
  switch (status) {
    case "active":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-400";
    case "triggered":
      return "border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan";
    case "cancelled":
      return "border-cyber-border bg-cyber-muted/10 text-cyber-muted";
    case "expired":
      return "border-amber-500/30 bg-amber-500/10 text-amber-400";
  }
}

export function describeTriggerResult(trigger: Job): string {
  const value = trigger.response ?? trigger.errorMessage ?? trigger.messageText;
  const singleLine = value.replace(/\s+/g, " ").trim();
  return singleLine.slice(0, 120) + (singleLine.length > 120 ? "..." : "");
}

function getString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
